/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AdcModel, BackupModel, DashboardDataModel, TierModel, WidgetModel } from '../../../../models';
import { AfterViewInit, Component } from '@angular/core';
import { DashboardService } from '../../../../services/component';
import { PortalRbacService } from '../../../../services/utils';
import { UtilsService } from '../../../../../../../core/services';
import { WidgetComponent } from '../widgetBaseComponent/widget.component';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-storage-widget',
    templateUrl: '../widgetBaseComponent/widget.component.html',
    styleUrls: ['../widgetBaseComponent/widget.component.scss']
})
export class StorageWidgetComponent extends WidgetComponent implements AfterViewInit {
    constructor(
        dashboardService: DashboardService,
        utilsService: UtilsService,
        private portalRbacService: PortalRbacService
    ) {
        super(dashboardService, utilsService);

        const data: DashboardDataModel = this.dashboardService.allData;
        this.setUpPage(data);
    }

    ngAfterViewInit(): void {
        this.dashboardService.allDataEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                data => {
                    this.setUpPage(data);
                }
            );
    }

    /**
     * Set up page.
     * @param data
     * @private
     */
    private setUpPage(data: any): void {
        const backupDetails: any = this.formatStorageData(data);
        this.view = this.getViewData(backupDetails);
        this.loaded = true;
    }

    /**
     * Format ADC data.
     * @private
     * @param backupList
     */
    private formatStorageData({ adcs, backups }): any {
        const backupDetails = { count: 0, adcCount: 0 };
        if (adcs?.length) {
            backupDetails.adcCount = adcs.length;
            const backupsForCurrentAdcs: any = adcs.map((adc: AdcModel) =>
                backups.filter((item: BackupModel) => item.adcID === adc.id).length
            );
            backupDetails.count = backupsForCurrentAdcs.reduce(
                (accumulator: any, currentValue: any) => accumulator + currentValue, 0
            );
        }

        return backupDetails;
    }

    /**
     * Get storage view data.
     * @param backupDetails
     * @private
     */
    private getViewData(backupDetails: { count: any; adcCount: any; }): WidgetModel {
        const selectedTier: TierModel = this.portalRbacService.selectedTier;
        let viewObject: WidgetModel = this.getBaseMessaging(this.baseView, selectedTier);

        const error: any = this.errors?.backups;
        if (error) {
            return this.getErrorMessaging(viewObject, error);
        }

        if (backupDetails.count) {
            viewObject = this.getActiveMessaging(viewObject, backupDetails);
        }

        if (selectedTier.adcs && selectedTier.backups) {
            const totalBackupsAllowed = backupDetails.adcCount * selectedTier.backups;
            if (this.percentageOfUsage(backupDetails.count, totalBackupsAllowed)) {
                viewObject = this.getNearLimitMessaging(viewObject, backupDetails);
            }

            if (backupDetails.count >= totalBackupsAllowed) {
                viewObject = this.getMaxedOutMessaging(viewObject, backupDetails);
            }
        }

        return viewObject;
    }

    /**
     * Get base view messaging.
     * @param baseView
     * @param tier
     * @private
     */
    private getBaseMessaging(baseView: WidgetModel, tier: TierModel): WidgetModel {
        const baseData = {
            iconSrc: 'assets/centralisedManagement/icons/storage.svg',
            title: 'storage',
            flagState: 'success',
            subtitle: 'Back up and restore your ADCs!',
            message: `Did you know you can back up your ADCs? You can have ${tier.backups} backups per ADC on your current subscription.`,
            linkText: 'Create a backup',
            linkUrl: '/cm/scheduler/schedules/new',
        };

        return this.updateObject(baseView, baseData);
    }

    /**
     * Get active messaging.
     * @param viewObject
     * @param backupDetails
     * @private
     */
    private getActiveMessaging(viewObject: WidgetModel, backupDetails: { count: any; adcCount: any; }): WidgetModel {
        const adcText: string = backupDetails.adcCount === 1
            ? `on ${backupDetails.adcCount} ADC`
            : `across ${backupDetails.adcCount} ADCs`;
        const backupText: string = backupDetails.adcCount === 1 ? 'backup' : 'backups';

        const newData = {
            flagState: 'hidden',
            subtitle: null,
            message: `
            You have ${backupDetails.count} ${backupText},  ${adcText}.`,
            linkText: 'View my Backups',
            linkUrl: '/cm/storage/backups',
            counters: [
                {
                    count: `${backupDetails.count}`,
                    countSubTitle: backupDetails.count === 1 ? 'Backup' : 'Backups',
                    subTitleClass: 'success'
                }
            ]
        };

        return this.updateObject(viewObject, newData);
    }

    /**
     * Get near limit messaging.
     * @param viewObject
     * @param backupDetails
     * @private
     */
    private getNearLimitMessaging(viewObject: WidgetModel, backupDetails: {
        count: any;
        adcCount?: any;
    }): WidgetModel {
        const newData = {
            flagState: 'warn',
            message: 'You have used all of your allocated backups. Please upgrade your subscription or contact sales.',
            counters: [
                {
                    count: `${backupDetails.count}`,
                    countSubTitle: backupDetails.count === 1 ? 'Backup' : 'Backups',
                    subTitleClass: 'warn'
                }
            ],
            hasSlider: true,
            sliderContent: {
                message: 'You are almost running out of storage, check out your upgrade options.'
            }
        };

        return this.updateObject(viewObject, newData);
    }

    /**
     * Get maxed out messaging.
     * @param viewObject
     * @param backupDetails
     * @private
     */
    private getMaxedOutMessaging(viewObject: WidgetModel, backupDetails: { count: any; adcCount?: any; }): WidgetModel {
        const newData = {
            flagState: 'error',
            message: 'You have used all of your allocated backups. Please upgrade your subscription or contact sales.',
            linkText: 'Upgrade options',
            linkUrl: '/cm/organisation/subscriptions',
            counters: [
                {
                    count: `${backupDetails.count}`,
                    countSubTitle: backupDetails.count === 1 ? 'Backup' : 'Backups',
                    subTitleClass: 'tertiary'
                }
            ],
            hasSlider: true,
            sliderContent: {
                message: 'You have maxed out on your storage, check out your upgrade options!'
            }
        };

        return this.updateObject(viewObject, newData);
    }
}
